import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion as m } from "motion/react";
import "./phone.css";
import type { GuessResult, RoomView, TeamId } from "../../shared/game-types.js";
import {
  isIdentityUsable,
  loadIdentity,
  request,
  saveIdentity,
  useConnection,
  useCountdown,
  useGuessResults,
  useRoom,
} from "../lib/socket.js";
import { useMotionTuning, useRules } from "../lib/tuning.js";
import { MAX_TEAM_NAME } from "../../shared/engine/engine.js";
import { DriftMarks } from "../DriftMarks.js";
import { usePhaseProgress } from "../tv/NextUpBar.js";

const VERDICT_COPY: Record<
  GuessResult["verdict"],
  { text: string; tone: "good" | "bad" | "warn" }
> = {
  correct: { text: "On the board", tone: "good" },
  duplicate: { text: "Already taken — try another", tone: "warn" },
  "near-miss": { text: "Just off the board", tone: "warn" },
  wrong: { text: "Not on this one", tone: "bad" },
  timeout: { text: "Out of time", tone: "bad" },
  // Wording is rule-agnostic on purpose: passing hands over either way, and
  // what it costs is already spelled out on the button that sent it.
  pass: { text: "Passed — handed over", tone: "bad" },
};

export function PhoneApp({ onExit }: { onExit?: () => void } = {}) {
  const room = useRoom();
  const connection = useConnection();
  const rules = useRules();
  const motion = useMotionTuning();
  // The test harness drives phones through query params: seat scopes storage,
  // and name/team make the frame join itself so you never hand-fill four forms.
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const seat = params.get("seat");
  const presetTeam = params.get("team") as TeamId | null;
  const presetName = params.get("name");

  const urlRoom = params.get("room")?.toUpperCase() ?? "";

  const [identity, setIdentity] = useState(() => {
    const stored = loadIdentity(seat);
    if (isIdentityUsable(stored, urlRoom)) return stored;
    saveIdentity(null, seat);
    return null;
  });
  const [code, setCode] = useState(() => urlRoom);
  const [name, setName] = useState(presetName ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [guess, setGuess] = useState("");
  const [feedback, setFeedback] = useState<GuessResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const me = room?.players.find((p) => p.id === identity?.playerId) ?? null;
  const myTurn = Boolean(
    me && room?.activePlayerId === me.id && room?.phase === "playing",
  );
  const remaining = useCountdown(myTurn ? (room?.deadline ?? null) : null);

  // Resume an existing identity after a reload so a mid-match refresh is harmless.
  useEffect(() => {
    if (connection !== "online" || !identity) return;
    request("phone:resume", {
      code: identity.code,
      playerId: identity.playerId,
    }).catch(() => {
      saveIdentity(null, seat);
      setIdentity(null);
    });
  }, [connection, identity, seat]);

  // Harness seats join themselves and take the side they were told to.
  // Guarded by a ref rather than a cancellation flag: StrictMode runs effects
  // twice on the same instance, and a cancelled first join still reaches the
  // server, leaving an orphan player nobody is driving.
  const joinedForRoom = useRef<string | null>(null);
  useEffect(() => {
    if (connection !== "online" || identity || !presetName || !code) return;
    if (joinedForRoom.current === code) return;
    joinedForRoom.current = code;
    void (async () => {
      try {
        const data = await request<{ playerId: string }>("phone:join", {
          code,
          name: presetName,
        });
        const next = { code, playerId: data.playerId, name: presetName };
        saveIdentity(next, seat);
        setIdentity(next);
        if (presetTeam) await request("phone:team", { teamId: presetTeam });
      } catch (e) {
        joinedForRoom.current = null;
        setError((e as Error).message);
      }
    })();
  }, [connection, identity, presetName, presetTeam, code, seat]);

  // The harness can type into this phone for you: clicking an answer in its
  // key posts it here rather than making you retype a country name.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "factfeud:ban") {
        void request("phone:ban", {
          categoryId: String(e.data.categoryId),
        }).catch(() => undefined);
        return;
      }
      if (e.data?.type === "factfeud:ready") {
        // Goes through the real socket event, so the harness exercises the same
        // path a thumb does rather than a shortcut around it.
        void request("phone:ready", { ready: Boolean(e.data.ready) }).catch(
          () => undefined,
        );
        return;
      }
      if (e.data?.type !== "factfeud:fill" || typeof e.data.text !== "string")
        return;
      setGuess(e.data.text);
      inputRef.current?.focus();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  useGuessResults(
    useCallback(
      (r: GuessResult) => {
        if (!identity || r.playerId !== identity.playerId) return;
        setFeedback(r);
        setTimeout(() => setFeedback(null), 2200);
      },
      [identity],
    ),
  );

  // Focus the field the moment the turn arrives; nobody should lose clock to a tap.
  useEffect(() => {
    if (myTurn) {
      setGuess("");
      inputRef.current?.focus();
      if ("vibrate" in navigator) navigator.vibrate?.(35);
    }
  }, [myTurn]);

  const join = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await request<{ playerId: string }>("phone:join", {
        code: code.trim().toUpperCase(),
        name: name.trim(),
      });
      const next = {
        code: code.trim().toUpperCase(),
        playerId: data.playerId,
        name: name.trim(),
      };
      saveIdentity(next, seat);
      setIdentity(next);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = guess.trim();
    if (!text) return;
    setGuess("");
    try {
      await request("phone:guess", { text });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const pass = async () => {
    setGuess("");
    try {
      await request("phone:pass", {});
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const pickTeam = async (teamId: TeamId) => {
    try {
      await request("phone:team", { teamId });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const banCategory = async (categoryId: string) => {
    try {
      await request("phone:ban", { categoryId });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const renameTeam = async (name: string) => {
    try {
      await request("phone:teamName", { name });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const setReady = async (ready: boolean) => {
    try {
      await request("phone:ready", { ready });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // ----------------------------------------------------------------- join

  if (!identity || !me) {
    return (
      <div className="phone grain">
        <DriftMarks />
        <div className="phone__head">
          {onExit ? (
            <button className="phoneBack" onClick={onExit}>
              <span aria-hidden="true">←</span> Start
            </button>
          ) : null}
          <span className="phone__code">
            <span className="dot" data-state={connection} />
            Join a game
          </span>
        </div>
        <m.div
          className="panel"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 280, damping: 22 }}
        >
          <h1>Join the game</h1>
          <p>Enter the code showing on the TV.</p>
          <input
            className="field field--code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 4))}
            placeholder="CODE"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            aria-label="Room code"
          />
          <input
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 16))}
            placeholder="Your name"
            aria-label="Your name"
            onKeyDown={(e) => {
              if (e.key === "Enter" && code.length === 4 && name.trim())
                void join();
            }}
          />
          {error ? <div className="error">{error}</div> : null}
          <button
            className="btn btn--primary"
            disabled={busy || code.trim().length < 4 || !name.trim()}
            onClick={join}
          >
            {busy ? "Joining…" : "Join"}
          </button>
        </m.div>
        <div />
      </div>
    );
  }

  // -------------------------------------------------------------- in room

  const teamCount = (id: TeamId) =>
    room?.players.filter((p) => p.teamId === id).length ?? 0;

  const present = room?.players.filter((p) => p.connected) ?? [];
  const readyTally = {
    ready: present.filter((p) => p.ready).length,
    total: present.length,
  };
  const canStart = teamCount("rose") > 0 && teamCount("slate") > 0;

  return (
    // The whole device wears the player's colour, so a glance at someone else's
    // phone tells you which side they are on.
    <div className="phone grain teamTint" data-team={me.teamId}>
      <DriftMarks />
      <div className="phone__head phone__head--team">
        {onExit ? (
          <button className="phoneBack" onClick={onExit}>
            <span aria-hidden="true">←</span> Start
          </button>
        ) : null}
        <span className="phone__code">
          <span className="dot" data-state={connection} />
          {identity.code}
        </span>
        <span className="phone__me">{me.name}</span>
      </div>

      <div
        className={`panel${room?.phase === "category-ban" ? " panel--fill" : ""}`}
      >
        {room?.phase === "category-ban" ? (
          <>
            <h1>
              {room.bansRemaining[me.teamId] > 0
                ? "Ban a category"
                : "Ban locked in"}
            </h1>
            <p>
              {room.bansRemaining[me.teamId] > 0
                ? "Hold the one your team least wants to face. Anyone can do it."
                : "Waiting for the other team."}
            </p>
            <div className="banList">
              {room.offers.map((offer) => (
                <BanItem
                  key={offer.id}
                  offer={offer}
                  bannedByName={
                    offer.bannedBy ? room.teams[offer.bannedBy].name : null
                  }
                  disabled={
                    Boolean(offer.bannedBy) ||
                    room.bansRemaining[me.teamId] === 0
                  }
                  holdMs={motion.pass.holdDuration_ms}
                  settleMs={motion.pass.settleDuration_ms}
                  onBan={() => banCategory(offer.id)}
                />
              ))}
            </div>
          </>
        ) : room?.phase === "lobby" ? (
          <>
            <h1>Pick your side</h1>
            <div className="teamPick">
              {(["rose", "slate"] as TeamId[]).map((id) => (
                <button
                  key={id}
                  className="teamBtn"
                  data-team={id}
                  data-selected={me.teamId === id}
                  onClick={() => pickTeam(id)}
                >
                  <strong>{room.teams[id].name}</strong>
                  <span>
                    {teamCount(id)} player{teamCount(id) === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
            </div>
            <TeamNameField
              teamId={me.teamId}
              name={room.teams[me.teamId].name}
              onRename={renameTeam}
            />
            <button
              className={`btn readyBtn ${me.ready ? "readyBtn--on" : "btn--primary"}`}
              onClick={() => setReady(!me.ready)}
            >
              {me.ready ? "Ready ✓" : "I'm ready"}
            </button>
            <p className="readyTally">
              {readyTally.ready} of {readyTally.total} ready
              {readyTally.ready === readyTally.total && !canStart
                ? " — both teams need a player"
                : ""}
            </p>
          </>
        ) : room?.phase === "match-end" ? (
          <div className="waiting">
            <h1>
              {room.winner === "tie"
                ? "A dead heat"
                : `${room.teams[room.winner ?? "rose"].name} take it`}
            </h1>
            <p>
              {room.teams.rose.name} {room.teams.rose.score} ·{" "}
              {room.teams.slate.name} {room.teams.slate.score}
            </p>
            <p>You found {me.correctCount} answers.</p>
          </div>
        ) : myTurn ? (
          <m.form
            className="yourTurn teamTint"
            data-team={me.teamId}
            onSubmit={submit}
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            style={{ ["--team" as string]: `var(--c-${me.teamId})` }}
          >
            <div className="mini">
              <span>Your turn</span>
              <span>{Math.ceil(remaining)}s</span>
            </div>
            <div className="bar" data-urgent={remaining <= 5}>
              <i
                style={{ width: `${(remaining / rules.answerSeconds) * 100}%` }}
              />
            </div>
            <h1>{room?.category?.prompt}</h1>
            {room?.category?.subtitle ? <p>{room.category.subtitle}</p> : null}
            <input
              ref={inputRef}
              className="field"
              value={guess}
              onChange={(e) => setGuess(e.target.value)}
              placeholder="Type an answer"
              enterKeyHint="send"
              autoCorrect="off"
              autoCapitalize="words"
              spellCheck={false}
              aria-label="Your answer"
            />
            <button
              className="btn btn--primary"
              type="submit"
              disabled={!guess.trim()}
            >
              Lock it in
            </button>
          </m.form>
        ) : (
          <div
            className="waiting teamTint"
            data-team={me.teamId}
            data-ours={room?.activeTeam === me.teamId}
          >
            {/* Tinted by whoever is on the clock, not by whoever is holding the
                phone — the card around it already carries your own colour, and
                the two have to be able to disagree. */}
            <div
              className="turnFlag teamTint"
              data-team={room?.activeTeam ?? me.teamId}
            >
              {room
                ? `${room.teams[room.activeTeam].name} are up`
                : "Hold tight"}
            </div>
            <h1>{room?.category?.prompt ?? "Getting ready"}</h1>
            <p>
              {room?.phase === "category-reveal"
                ? "Category closed"
                : room?.activePlayerId
                  ? `${room.players.find((p) => p.id === room.activePlayerId)?.name ?? "Someone"} is answering`
                  : "Hold tight"}
            </p>
          </div>
        )}

        <AnimatePresence>
          {feedback ? (
            <m.div
              className="verdict"
              data-tone={VERDICT_COPY[feedback.verdict].tone}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {feedback.ambiguous
                ? "That fits more than one answer — be more specific"
                : VERDICT_COPY[feedback.verdict].text}
              {feedback.verdict === "correct" && feedback.points
                ? ` · +${feedback.points}`
                : ""}
              {feedback.nearMiss
                ? ` · ${feedback.nearMiss.text} was #${feedback.nearMiss.rank}`
                : ""}
            </m.div>
          ) : null}
        </AnimatePresence>

        {error ? <div className="error">{error}</div> : null}
      </div>

      <div>
        {/* Deliberately down here, a thumb's travel away from "Lock it in" —
            and held rather than tapped. Giving up should take a moment of
            intent, not a stray tap while reaching for the keyboard. */}
        {myTurn ? (
          <HoldToPass
            onComplete={pass}
            holdMs={motion.pass.holdDuration_ms}
            settleMs={motion.pass.settleDuration_ms}
            costsStrike={rules.passCostsStrike}
          />
        ) : null}
        {room ? <PhoneNextUp room={room} /> : null}
        <div className="mini">
          <span>
            {room?.teams.rose.name} {room?.teams.rose.score}
          </span>
          <span>
            {room?.teams.slate.name} {room?.teams.slate.score}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Long enough to coalesce a burst of typing into one message, short enough that
 * the TV is still keeping up with the thumb rather than reporting on it.
 */
const NAME_ECHO_MS = 220;

/**
 * Rename your own side, live on the TV as you type.
 *
 * The draft is local state while the field has focus. The room's name is
 * authoritative and arrives back over the socket, so binding the input straight
 * to it would fight the typist by a round-trip on every keystroke — but only
 * echoing on blur left the TV a beat behind the person holding the phone, which
 * is worse. So: type freely, send on a short debounce, and resync from the room
 * only when this field is not the one being edited.
 */
function TeamNameField({
  teamId,
  name,
  onRename,
}: {
  teamId: TeamId;
  name: string;
  onRename: (name: string) => void;
}) {
  const [draft, setDraft] = useState(name);
  const [editing, setEditing] = useState(false);
  const echo = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!editing) setDraft(name);
  }, [name, editing]);

  // A pending echo must not outlive the field, or it renames the team after the
  // lobby has already moved on.
  useEffect(() => {
    return () => {
      if (echo.current) clearTimeout(echo.current);
    };
  }, []);

  const send = (value: string) => {
    if (echo.current) clearTimeout(echo.current);
    echo.current = setTimeout(() => onRename(value), NAME_ECHO_MS);
  };

  const commit = () => {
    setEditing(false);
    if (echo.current) clearTimeout(echo.current);
    if (draft.trim() !== name) onRename(draft);
  };

  return (
    <label className="teamName teamTint" data-team={teamId}>
      <span className="teamName__label">Your team</span>
      <input
        className="teamName__field"
        value={draft}
        maxLength={MAX_TEAM_NAME}
        onChange={(e) => {
          setDraft(e.target.value);
          send(e.target.value);
        }}
        onFocus={() => setEditing(true)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        aria-label="Your team's name"
        autoCapitalize="words"
        autoCorrect="off"
        spellCheck={false}
      />
    </label>
  );
}

/**
 * Pass, but you have to mean it.
 *
 * A tap here throws away the rest of your turn, and it sits within reach of the
 * keyboard, so it is held rather than tapped: the fill has to run the whole way
 * before the pass is sent. Letting go early costs nothing and rewinds.
 */
function HoldToPass({
  onComplete,
  holdMs,
  settleMs,
  costsStrike,
}: {
  onComplete: () => void;
  holdMs: number;
  settleMs: number;
  costsStrike: boolean;
}) {
  const { progress, held, bind } = useHold(holdMs, onComplete);

  return (
    <button
      className="holdPass"
      type="button"
      data-held={held}
      {...bind}
      aria-label={
        costsStrike
          ? "Hold to pass — costs a miss"
          : "Hold to pass — no miss, next player takes over"
      }
    >
      <span
        className="holdPass__fill"
        style={{
          width: `${progress * 100}%`,
          transitionDuration: held ? "0ms" : `${settleMs}ms`,
        }}
        aria-hidden="true"
      />
      <span className="holdPass__label">
        {held ? "Keep holding…" : "Hold to pass"}
      </span>
      <span className="holdPass__note">
        {costsStrike ? "costs a miss" : "no miss — next player takes over"}
      </span>
    </button>
  );
}

/**
 * Press-and-hold, as a hook.
 *
 * Both places that use it are irreversible and one tap from a mis-tap: passing
 * throws away your turn, banning takes a theme off the table for the whole
 * match. Returns the fill fraction, whether it is being held, and the props to
 * spread onto the button.
 */
function useHold(holdMs: number, onComplete: () => void) {
  const [progress, setProgress] = useState(0);
  const [held, setHeld] = useState(false);
  const frame = useRef<number | null>(null);
  const startedAt = useRef(0);
  // Guards the double-fire when the last frame lands on the same tick as the
  // pointerup that follows it.
  const fired = useRef(false);

  const cancelFrame = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
  }, []);

  const release = useCallback(() => {
    cancelFrame();
    setHeld(false);
    setProgress(0);
  }, [cancelFrame]);

  const tick = useCallback(() => {
    const elapsed = performance.now() - startedAt.current;
    const p = holdMs <= 0 ? 1 : Math.min(1, elapsed / holdMs);
    setProgress(p);
    if (p < 1) {
      frame.current = requestAnimationFrame(tick);
      return;
    }
    if (!fired.current) {
      fired.current = true;
      if ("vibrate" in navigator) navigator.vibrate?.([25, 40, 25]);
      onComplete();
    }
    release();
  }, [holdMs, onComplete, release]);

  const start = useCallback(() => {
    if (held) return;
    fired.current = false;
    startedAt.current = performance.now();
    setHeld(true);
    frame.current = requestAnimationFrame(tick);
  }, [held, tick]);

  // A hold interrupted by a re-render, an unmount, or the phase simply changing
  // must not leave a frame loop running against a dead component.
  useEffect(() => cancelFrame, [cancelFrame]);

  const bind = {
    // Pointer events cover thumb and mouse in one path; `touch-action: none` in
    // the stylesheet keeps the hold from being stolen by the scroll recogniser.
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      start();
    },
    onPointerUp: release,
    onPointerCancel: release,
    onLostPointerCapture: release,
    // Space and Enter repeat while held down, so the first one starts the clock
    // and the rest are no-ops.
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        start();
      }
    },
    onKeyUp: release,
    onBlur: release,
  };

  return { progress, held, bind };
}

/**
 * One theme on the ban board.
 *
 * Held rather than tapped: a ban is irreversible, it is one of only two your
 * team gets, and the list is scrolled with the same thumb that strikes it out.
 */
function BanItem({
  offer,
  bannedByName,
  disabled,
  holdMs,
  settleMs,
  onBan,
}: {
  offer: RoomView["offers"][number];
  bannedByName: string | null;
  disabled: boolean;
  holdMs: number;
  settleMs: number;
  onBan: () => void;
}) {
  const { progress, held, bind } = useHold(holdMs, onBan);

  return (
    <button
      className="banItem"
      data-banned={Boolean(offer.bannedBy)}
      data-held={held}
      disabled={disabled}
      {...(disabled ? {} : bind)}
    >
      <span
        className="banItem__fill"
        style={{
          width: `${progress * 100}%`,
          transitionDuration: held ? "0ms" : `${settleMs}ms`,
        }}
        aria-hidden="true"
      />
      <strong>{offer.label}</strong>
      <span>
        {bannedByName
          ? `banned by ${bannedByName}`
          : held
            ? "keep holding…"
            : offer.topic}
      </span>
    </button>
  );
}

/**
 * Countdown to whatever happens next, so a player staring at their phone
 * between turns knows the screen is about to move rather than wondering if the
 * game has stalled.
 */
function PhoneNextUp({ room }: { room: RoomView }) {
  const { remaining, fraction, label } = usePhaseProgress(room);
  if (!label || room.deadline === null) return null;
  const urgent = fraction <= 0.25;

  return (
    <div className="phoneNext" data-urgent={urgent}>
      <div className="phoneNext__label">
        <span>{label}</span>
        <span>{Math.ceil(remaining)}s</span>
      </div>
      <div className="phoneNext__track">
        <div
          className="phoneNext__fill"
          style={{ width: `${fraction * 100}%` }}
        />
      </div>
    </div>
  );
}
